import type { PresenceStatus } from '@novachat/shared';
import { useEffect, useRef } from 'react';
import { avatarColors, initials } from '../lib/avatar';
import { useLevels } from '../state/voice';

const presenceLabel: Record<PresenceStatus, string> = {
  online: 'Çevrimiçi',
  idle: 'Boşta',
  dnd: 'Rahatsız etmeyin',
  offline: 'Çevrimdışı',
};
const presenceColor: Record<PresenceStatus, string> = {
  online: 'bg-status-online',
  idle: 'bg-status-idle',
  dnd: 'bg-status-dnd',
  offline: 'bg-surface-0 ring-2 ring-inset ring-status-offline',
};

export function presenceText(status: PresenceStatus) {
  return presenceLabel[status];
}

interface AvatarProps {
  id: string;
  name: string;
  /** Pixels, or 'fluid' to follow the enclosing size container (voice tiles). */
  size?: number | 'fluid';
  speaking?: boolean;
  large?: boolean;
  presence?: PresenceStatus;
  className?: string;
}

/** Initials on the person's colour; the rotating ring shows who is speaking, and in a tile how loud. */
export function Avatar({ id, name, size = 24, speaking = false, large = false, presence, className = '' }: AvatarProps) {
  const { bg, fg } = avatarColors(id);
  const box = useRef<HTMLSpanElement>(null);
  // The voice level (0–1) goes straight into a CSS variable for the ring: no re-render per tick.
  useEffect(() => {
    const element = box.current;
    if (!speaking || !element) return;
    const show = (levels: Readonly<Record<string, number>>) => element.style.setProperty('--nc-level', (levels[id] ?? 0).toFixed(2));
    show(useLevels.getState().levels);
    const unsubscribe = useLevels.subscribe((s) => show(s.levels));
    return () => {
      unsubscribe();
      element.style.removeProperty('--nc-level');
    };
  }, [id, speaking]);
  // Fluid avatars take 42% of the tile's shorter side, between 36 and 112px.
  const side = size === 'fluid' ? 'var(--av)' : `${size}px`;
  const fontRatio = large ? 0.4 : 0.46;
  return (
    <span
      ref={box}
      className={`relative inline-grid flex-none ${className}`}
      style={{ ...(size === 'fluid' ? { ['--av' as string]: 'clamp(36px, 42cqmin, 112px)' } : {}), width: side, height: side }}
    >
      <span
        className={`grid place-items-center font-bold ${speaking ? (large ? 'nc-speaking nc-speaking-lg' : 'nc-speaking') : ''} ${large ? 'font-heading' : ''}`}
        style={{ width: side, height: side, borderRadius: `calc(${side} * 0.31)`, background: bg, color: fg, fontSize: `calc(${side} * ${fontRatio})` }}
        aria-hidden="true"
      >
        {initials(name)}
      </span>
      {presence && (
        <span
          role="img"
          aria-label={presenceLabel[presence]}
          className={`absolute -right-[3px] -bottom-[3px] rounded-full border-2 border-surface-0 ${presenceColor[presence]}`}
          style={{ width: `max(10px, calc(${side} * 0.42))`, height: `max(10px, calc(${side} * 0.42))` }}
        />
      )}
    </span>
  );
}
